/**
 * InvoiceSummaryCards.tsx
 *
 * Top summary row showing:
 *  Total Invoiced | Paid | Outstanding | Due count | Paid count
 * Updates live when filters change.
 */

import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
import AccountBalanceIcon  from "@mui/icons-material/AccountBalance";
import AssignmentIcon      from "@mui/icons-material/Assignment";
import CheckCircleIcon     from "@mui/icons-material/CheckCircle";
import TrendingUpIcon      from "@mui/icons-material/TrendingUp";
import WarningAmberIcon    from "@mui/icons-material/WarningAmber";

const fmt = (n: number) =>
  `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

interface Stats {
  totalGrand:  number;
  totalPaid:   number;
  totalDue:    number;
  countDue:    number;
  countPaid:   number;
  total:       number;
}

interface Props {
  stats:    Stats;
  loading:  boolean;
  isMobile: boolean;
}

const statCards = (stats: Stats) => [
  {
    label:  "Total Invoiced",
    value:  fmt(stats.totalGrand),
    sub:    `${stats.total} invoice${stats.total !== 1 ? "s" : ""}`,
    color:  "primary.main",
    border: "primary.light",
    icon:   <AssignmentIcon color="primary" />,
  },
  {
    label:  "Amount Received",
    value:  fmt(stats.totalPaid),
    sub:    `${stats.countPaid} settled`,
    color:  "success.main",
    border: "success.light",
    icon:   <CheckCircleIcon color="success" />,
  },
  {
    label:  "Outstanding Due",
    value:  fmt(stats.totalDue),
    sub:    `${stats.countDue} pending`,
    color:  stats.totalDue > 0 ? "error.main" : "success.main",
    border: stats.totalDue > 0 ? "error.light" : "success.light",
    icon:   stats.totalDue > 0
      ? <WarningAmberIcon color="error" />
      : <AccountBalanceIcon color="success" />,
  },
];

const InvoiceSummaryCards: React.FC<Props> = ({ stats, loading, isMobile }) => (
  <Grid container spacing={1.5} mb={2}>
    {loading
      ? [1, 2, 3].map((k) => (
          <Grid item xs={12} sm={4} key={k}>
            <Skeleton variant="rounded" height={80} sx={{ borderRadius: 2 }} />
          </Grid>
        ))
      : statCards(stats).map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <Card
              variant="outlined"
              sx={{ borderRadius: 2, borderColor: card.border, height: "100%" }}
            >
              <CardContent sx={{ p: isMobile ? 1.5 : 2, "&:last-child": { pb: isMobile ? 1.5 : 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {card.label}
                    </Typography>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      fontWeight={700}
                      color={card.color}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.sub}
                    </Typography>
                  </Box>
                  <Box sx={{ opacity: 0.7, mt: 0.5 }}>{card.icon}</Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
  </Grid>
);

export default InvoiceSummaryCards;